import { useState } from 'react';
import { Images } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '@utils/api';
import { formatInboxTime } from '@utils/helpers';
import { useNotifications } from '@context/NotificationContext';
import { INBOX_READ_ROW, INBOX_ROW_FRAME, INBOX_UNREAD_ROW } from '@components/messages/inboxRowStyles';

const ShareRequestInboxCard = ({ n, onShareHandled, isPulsing = false }) => {
  const { refreshUnreadCount } = useNotifications();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const activityTitle = n.activityId?.title || 'a vibe';
  const senderName = n.senderId?.name || 'Someone';
  const photoCount = n.photoCount || 0;
  const shareRequestId = n.shareRequestId?._id || n.shareRequestId;
  const notifId = n._id ?? n.id;
  const unread = !n.isRead && !done;

  const handle = async (action) => {
    if (!shareRequestId || busy) return;
    setBusy(true);
    try {
      await api.post(`/memories/share-requests/${shareRequestId}/${action}`);
      if (notifId) {
        await api.patch(`/notifications/${notifId}/read`).catch(() => {});
      }
      setDone(action);
      await onShareHandled?.(notifId);
      await refreshUnreadCount();
      toast.success(action === 'accept' ? 'Photos added to your profile' : 'Share declined');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`${INBOX_ROW_FRAME} ${unread ? INBOX_UNREAD_ROW : INBOX_READ_ROW} ${
        isPulsing ? 'animate-pulse-glow' : ''
      }`}
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-100 dark:bg-brand-500/20">
        <Images className="text-brand-600 dark:text-brand-300" size={22} strokeWidth={1.8} aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-surface-900 dark:text-surface-50">
            <span className="text-brand-700 dark:text-brand-300">Shared Memories</span>
            <span className="mx-1.5 font-normal text-surface-400 dark:text-surface-500">·</span>
            <span className="font-medium text-surface-800 dark:text-surface-200">{activityTitle}</span>
          </p>
          <span className="shrink-0 text-xs text-surface-400">{formatInboxTime(n.createdAt)}</span>
        </div>
        <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
          {n.message?.trim()
            || `${senderName} shared ${photoCount || ''} photo${photoCount === 1 ? '' : 's'} from ${activityTitle}.`}
        </p>

        {done ? (
          <p className="mt-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
            {done === 'accept' ? 'Accepted' : 'Declined'}
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy || !shareRequestId}
              onClick={() => handle('accept')}
              className="rounded-xl bg-brand-500 px-3.5 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-brand-600 disabled:opacity-50"
            >
              Accept
            </button>
            <button
              type="button"
              disabled={busy || !shareRequestId}
              onClick={() => handle('decline')}
              className="rounded-xl bg-surface-100 px-3.5 py-1.5 text-xs font-bold text-surface-700 transition hover:bg-surface-200 dark:bg-surface-800 dark:text-surface-200 dark:hover:bg-surface-700 disabled:opacity-50"
            >
              Decline
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareRequestInboxCard;
